/**
 * ASCII Art Generator - 状态管理 Store
 *
 * 使用 Zustand 管理全局状态，支持持久化
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { FullRenderSettings, StyleType } from '../core/types'
import type { EffectsState } from '../core/effects/types'
import { DEFAULT_SETTINGS } from '../core/defaults'
import { DEFAULT_EFFECTS_STATE } from '../core/effects/types'
import { getDefaultMaxChars } from '../core/performance/features'

// ============================================
// 预设类型定义
// ============================================

/** 预设类型 */
export interface Preset {
  id: string
  name: string
  description: string
  category: 'builtin' | 'user'
  createdAt: number
  settings: FullRenderSettings
  effectsState: EffectsState
}

// ============================================
// 性能配置类型定义
// ============================================

/** 性能配置 */
export interface PerformanceConfig {
  /** 字符数上限（用于分辨率限制） */
  maxCharsLimit: number
  /** 保持原尺寸（禁用自动缩放） */
  preserveOriginalSize: boolean
}

/** 默认性能配置 */
export const DEFAULT_PERFORMANCE_CONFIG: PerformanceConfig = {
  maxCharsLimit: getDefaultMaxChars(),
  preserveOriginalSize: false,
}

// ============================================
// Store 状态接口
// ============================================

/**
 * Store 状态接口
 */
interface AppState {
  // 渲染设置
  settings: FullRenderSettings

  // 特效状态
  effectsState: EffectsState

  // 性能配置
  performanceConfig: PerformanceConfig

  // 预设列表
  presets: Preset[]

  // 图像状态
  sourceImage: HTMLImageElement | null
  imageDataUrl: string | null

  // UI状态
  isLoading: boolean
  error: string | null

  // Settings Actions
  updateSettings: (settings: Partial<FullRenderSettings>) => void
  resetSettings: () => void
  setStyle: (style: StyleType) => void

  // Effects Actions
  updateEffectsState: (effectsState: Partial<EffectsState>) => void
  resetEffectsState: () => void
  setEffectEnabled: (effectType: string, enabled: boolean) => void
  updateEffectConfig: (effectType: string, config: Partial<any>) => void
  setEffectsOrder: (order: string[]) => void

  // Performance Actions
  updatePerformanceConfig: (config: Partial<PerformanceConfig>) => void
  resetPerformanceConfig: () => void
  setMaxCharsLimit: (limit: number) => void
  setPreserveOriginalSize: (preserve: boolean) => void

  // Presets Actions
  addPreset: (preset: Preset) => void
  removePreset: (presetId: string) => void
  loadPreset: (presetId: string) => void
  getCurrentPresetName: () => string | null

  // Unified Actions
  resetAll: () => void

  // Image Actions
  setSourceImage: (image: HTMLImageElement | null, dataUrl?: string) => void
  clearSourceImage: () => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
}

/**
 * 创建 Store
 */
export const useStore = create<AppState>()(
  persist(
    (set, get) => ({
      // 初始状态
      settings: { ...DEFAULT_SETTINGS },
      effectsState: { ...DEFAULT_EFFECTS_STATE },
      performanceConfig: { ...DEFAULT_PERFORMANCE_CONFIG },
      presets: [],
      sourceImage: null,
      imageDataUrl: null,
      isLoading: false,
      error: null,

      // Settings Actions
      updateSettings: (newSettings) =>
        set((state) => ({
          settings: { ...state.settings, ...newSettings },
        })),

      resetSettings: () =>
        set(() => ({
          settings: { ...DEFAULT_SETTINGS },
        })),

      setStyle: (style) =>
        set((state) => ({
          settings: { ...state.settings, style },
        })),

      // Effects Actions
      updateEffectsState: (newEffectsState) =>
        set((state) => ({
          effectsState: { ...state.effectsState, ...newEffectsState },
        })),

      resetEffectsState: () =>
        set(() => ({
          effectsState: { ...DEFAULT_EFFECTS_STATE },
        })),

      setEffectEnabled: (effectType, enabled) =>
        set((state) => ({
          effectsState: {
            ...state.effectsState,
            [effectType]: {
              ...state.effectsState[effectType as keyof EffectsState],
              enabled,
            },
          },
        })),

      updateEffectConfig: (effectType, config) =>
        set((state) => {
          const effectKey = effectType as keyof EffectsState
          const currentState = state.effectsState[effectKey]
          if (currentState && 'config' in currentState) {
            return {
              effectsState: {
                ...state.effectsState,
                [effectType]: {
                  ...currentState,
                  config: { ...currentState.config, ...config },
                },
              },
            }
          }
          return state
        }),

      setEffectsOrder: (order) =>
        set((state) => ({
          effectsState: { ...state.effectsState, order: order as any },
        })),

      // Performance Actions
      updatePerformanceConfig: (config) =>
        set((state) => ({
          performanceConfig: { ...state.performanceConfig, ...config },
        })),

      resetPerformanceConfig: () =>
        set(() => ({
          performanceConfig: { ...DEFAULT_PERFORMANCE_CONFIG },
        })),

      setMaxCharsLimit: (limit) =>
        set((state) => ({
          performanceConfig: { ...state.performanceConfig, maxCharsLimit: limit },
        })),

      setPreserveOriginalSize: (preserve) =>
        set((state) => ({
          performanceConfig: { ...state.performanceConfig, preserveOriginalSize: preserve },
        })),

      // Presets Actions
      addPreset: (preset) =>
        set((state) => ({
          presets: [...state.presets, preset],
        })),

      removePreset: (presetId) =>
        set((state) => ({
          presets: state.presets.filter((p) => p.id !== presetId),
        })),

      loadPreset: (presetId) =>
        set((state) => {
          const preset = state.presets.find((p) => p.id === presetId)
          if (preset) {
            return {
              settings: { ...preset.settings },
              effectsState: { ...preset.effectsState },
            }
          }
          return state
        }),

      getCurrentPresetName: () => {
        const state = get()
        // 检查是否有匹配的预设
        for (const preset of state.presets) {
          if (
            JSON.stringify(preset.settings) === JSON.stringify(state.settings) &&
            JSON.stringify(preset.effectsState) === JSON.stringify(state.effectsState)
          ) {
            return preset.name
          }
        }
        return null
      },

      // Unified Actions
      resetAll: () =>
        set(() => ({
          settings: { ...DEFAULT_SETTINGS },
          effectsState: { ...DEFAULT_EFFECTS_STATE },
          performanceConfig: { ...DEFAULT_PERFORMANCE_CONFIG },
        })),

      // Image Actions
      setSourceImage: (image, dataUrl) =>
        set(() => ({
          sourceImage: image,
          imageDataUrl: dataUrl || null,
          error: null,
        })),

      clearSourceImage: () =>
        set(() => ({
          sourceImage: null,
          imageDataUrl: null,
        })),

      setLoading: (loading) =>
        set(() => ({
          isLoading: loading,
        })),

      setError: (error) =>
        set(() => ({
          error,
        })),
    }),
    {
      name: 'ascii-art-settings',
      // 持久化设置、特效状态和预设
      partialize: (state) => ({
        settings: state.settings,
        effectsState: state.effectsState,
        performanceConfig: state.performanceConfig,
        presets: state.presets,
      }),
    }
  )
)

// 导出选择器hooks
export const useSettings = () => useStore((state) => state.settings)
export const useEffectsState = () => useStore((state) => state.effectsState)
export const usePerformanceConfig = () => useStore((state) => state.performanceConfig)
export const usePresets = () => useStore((state) => state.presets)
export const useSourceImage = () => useStore((state) => state.sourceImage)
export const useIsLoading = () => useStore((state) => state.isLoading)
export const useError = () => useStore((state) => state.error)